import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

import { DOCS_ENTRY } from '@/docs';
import { setupRouterGuards } from './guards';

/**
 * Portal 路由表 + 鉴权守卫
 *
 * 路由前缀（2026-08-16 重命名：URL 用产品/平台语义，不再暴露内部仓库名缩写）：
 * - /                    → Portal 首页（公开）
 * - /docs/*              → 使用文档页（公开，产品文档 + 使用指南）
 * - /login               → 全站统一登录页（邀请码，公开；两产品共用 JWT）
 * - /admin /admin/login  → 平台级管理后台（admin 角色）
 * - /jobfit              → 招聘岗位智能分析：产品页（公开）
 * - /jobfit/analyze      → 分析工具页（登录）
 * - /docsage             → DocSage：产品页（公开）
 * - /docsage/projects    → 控制台（游客浏览示例，动作时弹登录）
 * - /docsage/reader/:id  → 三栏阅读器（游客可读示例）
 *
 * 历史演进：W1 迁移时直接用模块代号 /wb（web-feedback）/ /ts（tender-sage）当
 * URL 前缀，产品品牌化后语义错位（内部仓库名暴露 + 登录/admin 归属错位），
 * 2026-08-16 统一重命名。旧路径全部保留 redirect 兼容老书签与外链。
 *
 * 注意：API 前缀 /wb-api / /ts-api 是代理层服务路由，本轮不改（不呈现给用户）。
 *
 * 鉴权字段（meta）：
 * - requiresAuth：true 表示需要登录
 * - requiresAdmin：true 表示仅 admin 角色
 * - title：文档标题
 *
 * 守卫实现：src/router/guards.ts
 */

/** jobfit 模块路由（原 /wb/*，web-feedback 迁移） */
const jobfitRoutes: RouteRecordRaw[] = [
  {
    // 产品营销页（公开）
    path: '/jobfit',
    name: 'jobfit-landing',
    component: () => import('@/views/wb/LandingView.vue'),
    meta: { title: '产品介绍 - 招聘岗位智能分析' },
  },
  {
    // 分析工具页（2026-08-16 游客浏览模式：页面公开，提交分析/OCR/简历上传/
    // 历史抽屉等动作在前端弹登录，分析类 API 后端仍强制鉴权）
    path: '/jobfit/analyze',
    name: 'jobfit-analyze',
    component: () => import('@/views/wb/HomeView.vue'),
    meta: { title: '岗位智能分析' },
  },
  {
    // onboarding 引导占位（PRD §9.1 决策 W1 占位，W2 实现）
    path: '/jobfit/onboarding',
    name: 'jobfit-onboarding',
    component: () => import('@/views/wb/PlaceholderView.vue'),
    meta: { requiresAuth: true, title: '新手引导' },
  },
];

/** docsage 模块路由（原 /ts/*，tender-sage 迁移） */
const docsageRoutes: RouteRecordRaw[] = [
  {
    // 产品营销页（公开）
    path: '/docsage',
    name: 'docsage-landing',
    component: () => import('@/views/ts/LandingView.vue'),
    meta: { title: '产品介绍 - DocSage 文档智能体' },
  },
  {
    // 控制台（2026-08-16 游客浏览模式：页面公开，上传/删除/重试动作时弹登录）
    path: '/docsage/projects',
    name: 'docsage-projects',
    component: () => import('@/views/ts/ProjectsView.vue'),
    meta: { title: '文档项目列表' },
  },
  {
    // Reader 页：docId → getProjectDetail → sections；切章节 → getSection
    path: '/docsage/reader/:docId',
    name: 'docsage-reader',
    component: () => import('@/views/ts/ReaderView.vue'),
    meta: { title: '文档阅读' },
  },
];

/** 旧路径兼容层（2026-08-16 重命名前的 URL，保留 redirect 不断老书签/外链） */
const legacyRedirects: RouteRecordRaw[] = [
  // 平台级旧路径
  { path: '/wb/login', redirect: '/login' },
  { path: '/wb/admin/login', redirect: '/admin/login' },
  { path: '/wb/admin', redirect: '/admin' },
  // jobfit 旧路径（原 /wb/*）
  { path: '/wb/home', redirect: '/jobfit/analyze' },
  { path: '/wb/landing', redirect: '/jobfit' },
  { path: '/wb/onboarding', redirect: '/jobfit/onboarding' },
  { path: '/wb/:pathMatch(.*)*', redirect: '/jobfit' },
  // docsage 旧路径（原 /ts/*）
  { path: '/ts/landing', redirect: '/docsage' },
  { path: '/ts/projects', redirect: '/docsage/projects' },
  {
    path: '/ts/reader/:docId',
    redirect: (to) => ({ path: `/docsage/reader/${to.params.docId}` }),
  },
  { path: '/ts/:pathMatch(.*)*', redirect: '/docsage' },
];

/** 更早期的老前端书签兼容（*.html 多页 URL，统一指到新路径） */
const legacyHtmlRedirects: RouteRecordRaw[] = [
  { path: '/admin-login.html', redirect: '/admin/login' },
  { path: '/admin-login', redirect: '/admin/login' },
  // /interview.html 已下线（PRD §9 决策不迁移），回工具页避免 404
  { path: '/interview.html', redirect: '/jobfit/analyze' },
  { path: '/interview', redirect: '/jobfit/analyze' },
  { path: '/landing.html', redirect: '/jobfit' },
  { path: '/landing', redirect: '/jobfit' },
  { path: '/job-fit', redirect: '/jobfit' },
  { path: '/job-fit.html', redirect: '/jobfit' },
  { path: '/login.html', redirect: '/login' },
  { path: '/app', redirect: '/jobfit/analyze' },
];

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'portal-home',
    component: () => import('@/views/PortalHomeView.vue'),
    meta: { title: 'Java 后端 · 全栈方向 · LLM 应用 · 个人主页' },
  },

  // ===== 文档模块（公开） =====
  {
    path: '/docs',
    redirect: DOCS_ENTRY,
  },
  {
    path: '/docs/:section/:page',
    name: 'docs-page',
    component: () => import('@/views/docs/DocsView.vue'),
    meta: { title: '使用文档' },
    // 故意不加 requiresAuth：公开文档页（用户已确认）
    // DocsView 内部对非法 section/page 会 replace 到 DOCS_ENTRY
  },

  // ===== 平台级：登录 + 管理后台 =====
  {
    // 全站统一登录入口（邀请码，两产品共用 JWT）
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { title: '登录' },
  },
  {
    // 管理后台（admin 角色）
    path: '/admin',
    name: 'admin',
    component: () => import('@/views/wb/AdminView.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: '管理后台' },
  },
  {
    // 管理员登录（公开，不走 requiresAdmin 守卫）
    path: '/admin/login',
    name: 'admin-login',
    component: () => import('@/views/wb/AdminLoginView.vue'),
    meta: { title: '管理员登录' },
  },

  // ===== 产品模块 =====
  ...jobfitRoutes,
  ...docsageRoutes,

  // ===== 兼容层 =====
  ...legacyRedirects,
  ...legacyHtmlRedirects,

  // 全站兜底
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition ?? { top: 0 };
  },
});

// 安装全局守卫（auth + admin 校验 + 标题同步）
setupRouterGuards(router);

export default router;
